<script setup lang="ts">
import { computed, ref } from "vue";
import {
  AtomText,
  SatIconButton,
  AtomIcon,
  SatButton,
  useImage,
} from "@chatfood/design-system";
import { getQueryParamValue } from "@chatfood/core-utils";
import type { RouteLocationNamedRaw } from "vue-router";
import { useModalHubStore } from "../modal-hub";
import { css } from "./css";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useBrandingStore } from "~/stores/branding";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { SalesChannel } from "~/domain/sales-channels";
import { SwitchLanguage } from "~/components/switch-language";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { useSalesChannel } from "~/composables/use-sales-channel";

const businessStore = useBusinessStore();
const outletStore = useOutletStore();
const basketStore = useBasketStore();
const customerStore = useCustomerStore();
const brandingStore = useBrandingStore();
const featureMatrixStore = useFeatureMatrixStore();

const modalHub = useModalHubStore();

const { t } = useLocale();
const { optimize } = useImage();
const { route } = useNavigation();
const { setSalesChannel } = useSalesChannel();

const isMenuOpen = ref(false);

const menuButtonIcon = computed(() => {
  return isMenuOpen.value ? "close" : "nav";
});

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  if (isMenuOpen.value) isMenuOpen.value = false;
};

const onWebOrdering = () => {
  setSalesChannel(SalesChannel.WEB_ORDERING);

  closeMenu();
};

const onCatering = () => {
  setSalesChannel(SalesChannel.CATERING);

  closeMenu();
};

const handleAuth = () => {
  modalHub.showAuthManager();
};

const isLoyaltyLinkVisible = computed(() => {
  return featureMatrixStore.isToggleEnabled("display-loyalty-menu-link");
});

const isMenuVisible = computed(() => {
  return (
    featureMatrixStore.isToggleEnabled("display-home-menu-link") ||
    featureMatrixStore.isToggleEnabled("display-locations-menu-link") ||
    isLoyaltyLinkVisible.value
  );
});

const cateringLink = computed<RouteLocationNamedRaw>(() => {
  if (
    businessStore.salesChannel === SalesChannel.CATERING &&
    outletStore.isLoaded &&
    basketStore.isLoaded
  ) {
    return {
      name: "outlet",
      params: {
        orderingMode: basketStore.orderingMode,
        outletSlug: outletStore.slug,
      },
    };
  }

  return { name: "order" };
});

const orderLink = computed<RouteLocationNamedRaw>(() => {
  if (
    businessStore.salesChannel !== SalesChannel.CATERING &&
    outletStore.isLoaded &&
    basketStore.isLoaded
  ) {
    return {
      name: "outlet",
      params: {
        orderingMode: basketStore.orderingMode,
        outletSlug: outletStore.slug,
      },
    };
  }

  return { name: "order" };
});

const isOrderLinkActive = computed(() => {
  if (businessStore.salesChannel === SalesChannel.CATERING) return false;
  if (route.value.name === "order") return true;

  const orderingMode = getQueryParamValue(route.value.params, "orderingMode");

  return Boolean(orderingMode);
});

const isCateringLinkActive = computed(() => {
  if (businessStore.salesChannel !== SalesChannel.CATERING) return false;
  if (route.value.name === "order") return true;

  const orderingMode = getQueryParamValue(route.value.params, "orderingMode");

  return Boolean(orderingMode);
});
</script>

<template>
  <header :class="[css(), isMenuVisible && css('', 'with-menu')]">
    <div :class="css('hamburger-button')">
      <button v-if="isMenuVisible" @click="toggleMenu">
        <AtomIcon
          :key="menuButtonIcon"
          :name="menuButtonIcon"
          :class="css('hamburger')"
        />
      </button>
    </div>
    <div :class="css('logo')">
      <NuxtLink
        v-if="brandingStore.businessPrimaryLogo"
        :to="{ name: 'order' }"
      >
        <img
          :src="
            optimize(brandingStore.businessPrimaryLogo, {
              h: 100,
            })
          "
          :alt="businessStore.name"
        />
      </NuxtLink>
    </div>
    <div :class="css('navbar')">
      <div
        v-if="isMenuVisible"
        :class="[css('menu'), isMenuOpen && css('menu', 'open')]"
      >
        <ul>
          <li
            v-if="featureMatrixStore.isToggleEnabled('display-home-menu-link')"
          >
            <NuxtLink
              :to="{ name: 'home' }"
              data-test="home"
              @click="closeMenu"
            >
              <AtomText type="body-bold">
                {{ t("components.web_header.home_label") }}
              </AtomText>
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              v-if="
                featureMatrixStore.isToggleEnabled('display-order-menu-link')
              "
              :to="orderLink"
              data-test="order"
              :class="{
                'router-link-active': isOrderLinkActive,
                'router-link-invalid': isCateringLinkActive,
              }"
              @click="onWebOrdering"
            >
              <AtomText type="body-bold">
                {{ t("components.web_header.order_label") }}
              </AtomText>
            </NuxtLink>
          </li>
          <li
            v-if="
              featureMatrixStore.isToggleEnabled('display-catering-menu-link')
            "
          >
            <NuxtLink
              :class="{
                'router-link-active': isCateringLinkActive,
                'router-link-invalid': isOrderLinkActive,
              }"
              :to="cateringLink"
              data-test="catering"
              @click="onCatering"
            >
              <AtomText type="body-bold">
                {{ t("components.web_header.catering_label") }}
              </AtomText>
            </NuxtLink>
          </li>
          <li
            v-if="
              featureMatrixStore.isToggleEnabled('display-locations-menu-link')
            "
          >
            <NuxtLink
              :to="{ name: 'locations' }"
              data-test="locations"
              @click="closeMenu"
            >
              <AtomText type="body-bold">
                {{ t("components.web_header.locations_label") }}
              </AtomText>
            </NuxtLink>
          </li>
          <li
            v-if="isLoyaltyLinkVisible"
            data-test="loyalty-program"
            @click="closeMenu"
          >
            <NuxtLink :to="{ name: 'loyalty-program' }">
              <AtomText type="body-bold">
                {{ t("components.web_header.loyalty_label") }}
              </AtomText>
            </NuxtLink>
          </li>
        </ul>
      </div>
      <div id="nav-append" :class="css('nav-append')" />
    </div>
    <div :class="css('actions')">
      <SwitchLanguage />
      <template v-if="customerStore.isLogged">
        <div :class="css('user-icon')">
          <SatIconButton
            :route="{ name: 'my-account.profile' }"
            icon-name="user"
            theme="ghost"
            data-test="profile-button"
          />
        </div>
        <div :class="css('user-button')">
          <SatButton :route="{ name: 'my-account.profile' }">
            {{ customerStore.name }}
          </SatButton>
        </div>
      </template>
      <template v-else-if="!featureMatrixStore.skipAuthFlow">
        <div :class="css('auth-icon')">
          <SatIconButton
            icon-name="user"
            theme="ghost"
            data-test="auth-icon"
            @click.prevent="handleAuth"
          />
        </div>
        <div :class="css('auth-button')">
          <SatButton data-test="auth-button" @click.prevent="handleAuth">
            {{ t("components.web_header.login_label") }}
          </SatButton>
        </div>
      </template>
    </div>
  </header>
</template>

<style lang="scss" scoped>
$child-min-height: 40px;

.web-header {
  $self: &;

  background-color: var(--color-background-shell);
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: start;
  flex-wrap: wrap;
  padding: var(--spacing-sm) var(--spacing-md);

  @media (width >= $breakpoint-sm) {
    box-shadow: var(--elevation-level-1);
    padding: var(--spacing-md) var(--spacing-lg);
    flex-wrap: unset;
  }

  &__navbar {
    order: 1;
    flex: 1 1 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    @media (width >= $breakpoint-sm) {
      order: unset;
      flex: unset;
      min-height: $child-min-height;
    }
  }

  &__hamburger-button {
    display: flex;
    flex: 1;
    min-height: $child-min-height;

    @media (width >= $breakpoint-sm) {
      display: none;
    }

    button {
      border: 0;
      background: 0;
      cursor: pointer;
      padding: var(--spacing-sm);
    }
  }

  &__logo {
    display: flex;
    flex: 1;
    justify-content: center;
    min-height: $child-min-height;

    @media (width >= $breakpoint-sm) {
      justify-content: flex-start;
    }

    img {
      height: 40px;
      width: auto;
      display: block;
    }
  }

  &__menu {
    display: block;
    transition: right 0.3s ease-in-out;
    position: fixed;
    top: 0;
    right: -100%;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background-color: var(--color-background-shell);
    padding: var(--spacing-xl);
    z-index: 1;
    box-shadow: var(--elevation-level-1);

    @media (width >= $breakpoint-sm) {
      display: flex;
      flex: 1;
      justify-content: center;

      // reset position fixed
      position: initial;
      top: initial;
      right: initial;
      width: initial;
      max-width: initial;
      height: initial;
      background-color: initial;
      padding: initial;
      z-index: initial;
      box-shadow: initial;
    }

    &--open {
      right: 0;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
      list-style: none;
      margin: 0;
      padding: 0;

      @media (width >= $breakpoint-sm) {
        flex-direction: row;
        gap: var(--spacing-lg);
      }
    }

    ul li a span {
      display: flex;
      transition: all 0.2s ease;
      border-bottom: 1px solid var(--color-base-95);
      padding-block: var(--spacing-sm);

      @media (width >= $breakpoint-sm) {
        padding-top: calc(var(--spacing-sm) + 2px);
        border-bottom: 2px solid transparent;
      }
    }

    ul li a {
      &:hover {
        span {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }
      }
    }

    ul li a:not(.router-link-invalid) {
      &.router-link-active,
      &.router-link-exact-active {
        span {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }
      }
    }
  }

  &__actions {
    display: flex;
    gap: var(--spacing-xs);
    justify-content: flex-end;
    align-items: center;
    flex: 1;
    min-width: fit-content;
    min-height: $child-min-height;
  }

  &__auth-icon,
  &__user-icon {
    @media (width >= $breakpoint-md) {
      display: none;
    }
  }

  &__auth-button,
  &__user-button {
    display: none;

    @media (width >= $breakpoint-md) {
      display: initial;
    }
  }

  &__nav-append > * {
    padding-inline: var(--spacing-md);
    margin-top: var(--spacing-sm);

    @media (width >= $breakpoint-sm) {
      margin-top: 0;
    }
  }

  &--with-menu {
    #{$self}__nav-append > * {
      @media (width >= $breakpoint-sm) {
        margin-top: var(--spacing-md);
      }
    }
  }
}
</style>
